import { AlertCircle, Eye, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { STATUS_CONFIG } from "@/lib/utils/agent-status";
import { ACTION_HANDLERS } from "@/lib/data/agent-actions";
import type { UnifiedAgentRow } from "@/lib/data/unified-agents";

interface NeedsAttentionSectionProps {
  rows: UnifiedAgentRow[];
  onFocus: (row: UnifiedAgentRow) => void;
}

export function NeedsAttentionSection({ rows, onFocus }: NeedsAttentionSectionProps) {
  if (rows.length === 0) return null;

  return (
    <div className="border-b border-border/40 px-2 pb-2">
      <div className="flex items-center gap-2 px-1 py-2">
        <AlertCircle className="h-3.5 w-3.5 text-[var(--colony-amber)]" />
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Needs attention
        </span>
        <span className="ml-auto rounded-full bg-muted px-1.5 text-xs tabular-nums">
          {rows.length}
        </span>
      </div>
      <ul className="space-y-1">
        {rows.map((row) => {
          const config = STATUS_CONFIG[row.status];
          const canRetry =
            row.source === "workflow" &&
            row.status === "error" &&
            !!ACTION_HANDLERS.workflow.resume;
          return (
            <li
              key={row.id}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50"
            >
              <span className={cn("h-2 w-2 shrink-0 rounded-full", config.dotClass)} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{row.title}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {row.projectName} · {config.label}
                </div>
              </div>
              {canRetry && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => void ACTION_HANDLERS.workflow.resume?.(row)}
                >
                  <RotateCcw className="mr-1 h-3 w-3" />
                  Retry
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={() => onFocus(row)}
              >
                <Eye className="mr-1 h-3 w-3" />
                View
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
